import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import MobileFrame from '@/components/MobileFrame';
import { Icon } from '@/components/Icons';
import { useT } from '@/lib/i18n';
import { useAuthStore } from '@/lib/auth-store';
import { login, me } from '@/lib/auth-api';

export default function LoginScreen() {
    const t = useT();
    const navigate = useNavigate();
    const setAuth = useAuthStore((s) => s.setAuth);

    const [identifier, setIdentifier] = useState('');
    const [password, setPassword] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [err, setErr] = useState<string | null>(null);

    const canSubmit = identifier.length > 0 && password.length >= 6 && !submitting;

    const handleSubmit = async (e: FormEvent) => {
        e.preventDefault();
        if (!canSubmit) return;
        try {
            setSubmitting(true);
            setErr(null);
            await login(identifier.trim(), password);
            const payload = await me();
            setAuth(payload.user, payload.registration);
            navigate('/app/dashboard', { replace: true });
        } catch (e) {
            setErr(e instanceof Error ? e.message : t.somethingWrong);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <MobileFrame showLangToggle>
            <form
                onSubmit={handleSubmit}
                className="fade-in"
                style={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    padding: '24px 24px 32px',
                }}
            >
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
                    <div
                        style={{
                            width: 40,
                            height: 40,
                            borderRadius: 10,
                            background:
                                'linear-gradient(135deg, var(--primary) 0%, var(--primary-dark) 100%)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}
                    >
                        <Icon name="lock" size={20} color="white" />
                    </div>
                    <h1 style={{ fontSize: 24, fontWeight: 700, letterSpacing: '-0.02em' }}>
                        Masuk Akun
                    </h1>
                </div>
                <p
                    style={{
                        fontSize: 14,
                        lineHeight: 1.55,
                        color: 'var(--ink-500)',
                        marginBottom: 28,
                    }}
                >
                    Masuk dengan email atau nomor HP yang kamu daftarkan di onboarding.
                </p>

                <label className="field-label" htmlFor="identifier">
                    Email atau Nomor HP
                </label>
                <input
                    id="identifier"
                    className="field-input"
                    value={identifier}
                    onChange={(e) => setIdentifier(e.target.value)}
                    placeholder="nama@email.com / 812xxx"
                    autoComplete="username"
                    autoFocus
                />

                <label className="field-label" htmlFor="password" style={{ marginTop: 14 }}>
                    Password
                </label>
                <input
                    id="password"
                    className="field-input"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    autoComplete="current-password"
                />

                {err && (
                    <div
                        className="chip chip-error"
                        style={{ marginTop: 14, alignSelf: 'flex-start' }}
                    >
                        <Icon name="alert" size={14} /> {err}
                    </div>
                )}

                <div style={{ flex: 1 }} />

                <button
                    type="submit"
                    className="btn btn-primary"
                    style={{ width: '100%', marginBottom: 10 }}
                    disabled={!canSubmit}
                >
                    {submitting ? <div className="spinner" /> : 'Masuk'}
                </button>
                <button
                    type="button"
                    className="btn btn-secondary"
                    style={{ width: '100%', marginBottom: 8 }}
                    onClick={() => navigate('/app/activate')}
                >
                    <Icon name="shieldCheck" size={16} color="var(--ink-700)" />
                    Sudah punya akun Peruri? Aktivasi
                </button>
                <button
                    type="button"
                    className="btn btn-ghost"
                    style={{ width: '100%' }}
                    onClick={() => navigate('/onboarding/welcome')}
                >
                    Belum punya akun? Daftar
                </button>
            </form>
        </MobileFrame>
    );
}
